import { Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, FormArray, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { eLearningNameToId, makeUniqueELearningId } from '../../utils/e-learning-name-to-id';
import { IDeliverableQuestion, DeliverableQuestionType } from '@shared';

@Component({
  selector: 'app-new-deliverable-question-form-dialog',
  templateUrl: './new-deliverable-question-form-dialog.component.html',
  styleUrls: ['./new-deliverable-question-form-dialog.component.scss']
})
export class NewDeliverableQuestionFormDialogComponent implements OnInit {
  deliverableQuestionForm: FormGroup;
  currentQuestionType: DeliverableQuestionType = 'freeText';
  blankCount = 0;

  questionTypes: { value: DeliverableQuestionType; label: string }[] = [
    { value: 'freeText', label: 'Texte libre' },
    { value: 'fillInTheBlank', label: 'Texte à trou' },
    { value: 'table', label: 'Tableau' },
    { value: 'imageUpload', label: 'Upload image' }
  ];

  constructor(
    private fb: FormBuilder,
    public dialogRef: MatDialogRef<NewDeliverableQuestionFormDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { elementInfo: IDeliverableQuestion; existingIds?: string[] }
  ) {
    this.deliverableQuestionForm = this.fb.group({
      mandatory: [false],
      question: ['', Validators.required],
      description: [''],
      questionType: ['freeText', Validators.required],
      templateText: [''],
      tableColumns: this.fb.array([]),
      tableRowLabels: this.fb.array([]),
      tableMinRows: [null],
      tableMaxRows: [null],
      allowAiGeneration: [true],
      firstColumnAsNumbering: [false]
    });
  }

  ngOnInit(): void {
    this.mapInfoToForm();
    this.onQuestionTypeChange();
  }

  get tableColumns(): FormArray {
    return this.deliverableQuestionForm.get('tableColumns') as FormArray;
  }

  get tableRowLabels(): FormArray {
    return this.deliverableQuestionForm.get('tableRowLabels') as FormArray;
  }

  onQuestionTypeChange(): void {
    this.currentQuestionType = this.deliverableQuestionForm.get('questionType')?.value;
  }

  addBlank(): void {
    this.blankCount++;
    const templateText = this.deliverableQuestionForm.get('templateText')?.value || '';
    this.deliverableQuestionForm.patchValue({
      templateText: templateText + `{{${this.blankCount}}}`
    });
  }

  addColumn(): void {
    const columnGroup = this.fb.group({
      label: ['', Validators.required],
      minItems: [null],
      maxItems: [null]
    });
    this.tableColumns.push(columnGroup);
  }

  removeColumn(index: number): void {
    this.tableColumns.removeAt(index);
  }

  addRowLabel(): void {
    this.tableRowLabels.push(this.fb.control('', Validators.required));
  }

  removeRowLabel(index: number): void {
    this.tableRowLabels.removeAt(index);
  }

  getGeneratedVariable(): string {
    const existingId = this.data?.elementInfo?.id;
    if (existingId) {
      const variable = existingId.replace(/-/g, '_').toUpperCase();
      return `{{${variable}}}`;
    }
    const question = this.deliverableQuestionForm.get('question')?.value;
    if (!question || question.trim() === '') return '';
    const id = eLearningNameToId(question);
    const variable = id.replace(/-/g, '_').toUpperCase();
    return `{{${variable}}}`;
  }

  mapInfoToForm(): void {
    const questionInfo = this.data?.elementInfo;
    if (questionInfo) {
      this.deliverableQuestionForm.patchValue({
        mandatory: questionInfo.mandatory,
        question: questionInfo.question,
        description: questionInfo.description || '',
        questionType: questionInfo.questionType,
        templateText: questionInfo.templateText || '',
        tableMinRows: questionInfo.tableMinRows,
        tableMaxRows: questionInfo.tableMaxRows,
        allowAiGeneration: questionInfo.allowAiGeneration ?? true,
        firstColumnAsNumbering: questionInfo.firstColumnAsNumbering ?? false
      });

      if (questionInfo.templateText) {
        const matches = questionInfo.templateText.match(/\{\{\d+\}\}/g);
        this.blankCount = matches ? matches.length : 0;
      }

      if (questionInfo.tableColumns) {
        questionInfo.tableColumns.forEach((col) => {
          const columnGroup = this.fb.group({
            label: [col.label, Validators.required],
            minItems: [col.minItems],
            maxItems: [col.maxItems]
          });
          this.tableColumns.push(columnGroup);
        });
      }

      if (questionInfo.tableRowLabels) {
        questionInfo.tableRowLabels.forEach((label) => {
          this.tableRowLabels.push(this.fb.control(label, Validators.required));
        });
      }

      this.currentQuestionType = questionInfo.questionType;
    }
  }

  onSubmit(): void {
    this.deliverableQuestionForm.markAllAsTouched();
    if (this.deliverableQuestionForm.invalid) {
      const errors: string[] = [];
      if (this.deliverableQuestionForm.get('question')?.invalid) {
        errors.push('Le champ "Question / Libellé" est obligatoire');
      }
      if (this.currentQuestionType === 'fillInTheBlank') {
        const templateText = this.deliverableQuestionForm.get('templateText')?.value || '';
        if (!templateText.trim()) {
          errors.push('Le texte à trou est obligatoire');
        } else if (!templateText.includes('{{')) {
          errors.push('Le texte à trou doit contenir au moins un trou (utilisez le bouton "Ajouter un trou")');
        }
      }
      if (this.currentQuestionType === 'table' && this.tableColumns.length === 0) {
        errors.push('Vous devez ajouter au moins une colonne pour un tableau');
      }
      alert(errors.length > 0 ? errors.join('\n') : 'Formulaire non valide');
      return;
    }

    if (this.currentQuestionType === 'fillInTheBlank') {
      const templateText = this.deliverableQuestionForm.get('templateText')?.value || '';
      if (!templateText.includes('{{')) {
        alert('Le texte à trou doit contenir au moins un trou (utilisez le bouton "Ajouter un trou")');
        return;
      }
    }

    const formValue = this.deliverableQuestionForm.value;
    const question: IDeliverableQuestion = {
      id: this.data?.elementInfo?.id
        || makeUniqueELearningId(eLearningNameToId(formValue.question), this.data?.existingIds ?? []),
      mandatory: formValue.mandatory,
      question: formValue.question,
      description: formValue.description || undefined,
      questionType: formValue.questionType
    };

    if (formValue.questionType === 'fillInTheBlank') {
      question.templateText = formValue.templateText;
    }

    if (formValue.questionType === 'table') {
      question.tableColumns = formValue.tableColumns;
      question.tableRowLabels = formValue.tableRowLabels.length > 0 ? formValue.tableRowLabels : undefined;
      question.tableMinRows = formValue.tableMinRows;
      question.tableMaxRows = formValue.tableMaxRows;
      question.firstColumnAsNumbering = formValue.firstColumnAsNumbering;
    }

    if (formValue.questionType === 'imageUpload') {
      question.allowAiGeneration = formValue.allowAiGeneration;
    }

    this.dialogRef.close({ element: { info: question, type: 'deliverableQuestion' }, operation: 'save' });
  }

  deleteQuestion(): void {
    this.dialogRef.close({ element: { info: this.data?.elementInfo, type: 'deliverableQuestion' }, operation: 'delete' });
  }

  closeDialog(): void {
    this.dialogRef.close({ operation: 'cancel' });
  }
}
